import * as SelectPrimitive from '@radix-ui/react-select'
import { cva, type VariantProps } from 'class-variance-authority'
import { ChevronDown, ChevronUp } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/utils'
import { buttonVariants } from '../ui/button'
import { Icon, type IconName } from '../ui/icon'
import { FollowWindowBreakpoint } from '../ui/use-responsive'

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

const selectTriggerVariants = cva(
  'flex h-auto w-full items-center justify-between rounded border px-3 py-2 ring-offset-background focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
  {
    variants: {
      variant: {
        default: 'text-header bg-sub-surface-bg data-[placeholder]:text-secondary',
        inverse:
          'text-header-inverse border-border-inverse bg-input-bg-inverse data-[placeholder]:text-secondary-inverse hover:border-secondary-inverse',
      },
      size: {
        default: 'h-14 text-base', // text-base 16px height 56px
      },
      weight: {
        default: '',
        normal: 'font-normal',
        semibold: 'font-semibold',
        bold: 'font-bold',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      weight: 'default',
    },
  },
)

const selectContentVariants = cva(
  'relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[placeholder]:text-secondary',
  {
    variants: {
      variant: {
        default: 'text-header border-border bg-background',
        inverse: 'text-header-inverse border-border-inverse bg-background-inverse',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

const selectItemVariants = cva(
  'relative flex w-full cursor-pointer select-none items-center rounded-sm px-8 py-2 outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
  {
    variants: {
      variant: {
        default: 'text-header border-border bg-background  focus:bg-hover-bg focus:text-header',
        inverse:
          'text-header-inverse border-border-inverse bg-background-inverse focus:bg-hover-bg-inverse focus:text-header-inverse',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

type ButtonVariant = VariantProps<typeof buttonVariants>['variant']
type ButtonSize = VariantProps<typeof buttonVariants>['size']

interface SelectTriggerProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>,
    VariantProps<typeof selectTriggerVariants> {
  iconRight?: React.ReactNode
  /** When set, trigger uses Button design system styles instead of selectTriggerVariants */
  buttonVariant?: ButtonVariant
  buttonSize?: ButtonSize
}

const SelectTrigger = React.forwardRef<React.ElementRef<typeof SelectPrimitive.Trigger>, SelectTriggerProps>(
  ({ className, iconRight, children, variant, size, weight, buttonVariant, buttonSize, ...props }, ref) => {
    /**
     * ButtonVariants take precedence over selectTriggerVariants
     */
    const triggerClassName =
      buttonVariant !== undefined && buttonSize !== undefined
        ? cn(buttonVariants({ variant: buttonVariant, size: buttonSize }), className)
        : cn(selectTriggerVariants({ variant, size, weight }), className)

    return (
      <SelectPrimitive.Trigger ref={ref} className={triggerClassName} {...props}>
        {children}
        {/* iconRight can be null to hide the icon */}
        {iconRight !== undefined ? (
          iconRight
        ) : (
          <SelectPrimitive.Icon asChild>
            <ChevronDown className="ml-4 h-4 w-4 opacity-50" />
          </SelectPrimitive.Icon>
        )}
      </SelectPrimitive.Trigger>
    )
  },
)
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> &
    VariantProps<typeof selectContentVariants> & {
      wrapperClassName?: string
    }
>(({ className, children, wrapperClassName, position = 'popper', variant, ...props }, ref) => (
  <SelectPrimitive.Portal>
    <div className={wrapperClassName}>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          selectContentVariants({ variant }),
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className,
        )}
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            'p-1',
            position === 'popper' &&
              'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]',
          )}
        >
          <FollowWindowBreakpoint>{children}</FollowWindowBreakpoint>
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </div>
  </SelectPrimitive.Portal>
))
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label ref={ref} className={cn('py-1.5 pl-8 pr-2 text-sm font-semibold', className)} {...props} />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> &
    VariantProps<typeof selectItemVariants> & {
      iconName?: IconName
      persistIcon?: boolean
      showIcon?: boolean
    }
>(({ className, children, variant, iconName, persistIcon = false, showIcon = true, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(selectItemVariants({ variant, className }), className, !showIcon && 'pl-2')}
    {...props}
  >
    {showIcon && (
      <span className="absolute left-2 flex items-center justify-center text-inherit">
        {persistIcon ? (
          <Icon name={iconName ?? 'Check'} className="h-4 w-4 text-inherit" />
        ) : (
          <SelectPrimitive.ItemIndicator>
            <Icon name={iconName ?? 'Check'} className="h-4 w-4 text-inherit" />
          </SelectPrimitive.ItemIndicator>
        )}
      </span>
    )}
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator ref={ref} className={cn('-mx-1 my-1 h-px bg-sub-surface-bg', className)} {...props} />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
}
